import React, { useState, useEffect } from "react";
import { MessageSquare, Shield, Lock, Loader2, Timer, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { useAppContext } from "../../context/AppContext";
import { API_ENDPOINTS } from "../../utils/constants";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { decryptFromBackend } from "../../utils/utilCrypto";
import { useLogger } from "../../utils/UnifiedLogger";
import PropTypes from "prop-types";
import useLocalization from "../../hooks/useLocalization";

const LoginPage = ({ onLogin }) => {
    const { logInfo } = useLogger("LoginPage", false);
    const { t } = useLocalization();

    logInfo('LOGIN_PAGE_RENDERED', 'Login page rendered');
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState("Initializing secure login...");
    const [error, setError] = useState("");
    const [redirectTimer, setRedirectTimer] = useState(5);
    const [showTimer, setShowTimer] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const payload = location.state?.payload;

    const { sharedApiService, login, logout, userData, setUserData, redirectTo } = useAppContext();
    const userLoginRequest = sharedApiService?.sendRequest;


    // Countdown timer effect
    useEffect(() => {
        if (showTimer && redirectTimer > 0) {
            const timer = setTimeout(() => {
                setRedirectTimer(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (showTimer && redirectTimer === 0) {
            window.location.href = redirectTo; // Redirect to PG Portal
        }
    }, [redirectTimer,redirectTo, showTimer]);

    useEffect(() => {
        let redirectTimerRef;

        const authenticate = async (payload) => {
            // console.log("🔑 Payload received:", payload);
            try {
                setStatusMessage("Preparing secure session...");

                if (userData?.id) {
                    await logout();
                    await new Promise((r) => setTimeout(r, 1000));
                }

                const parsedPayload = typeof payload === "string" ? JSON.parse(payload) : payload;
                const userId = parsedPayload.user_id;
                // console.log(`🔑 User ID: ${userId}`);
                if (!userId) throw new Error("User ID missing in payload.");

                setStatusMessage("Connecting to PG Portal...");

                userLoginRequest({
                    method: "GET",
                    url: `${API_ENDPOINTS.getUserProfileNIC}?userCode=${userId}`,
                    abortable: true,
                    isEncrypted: false,
                    onSuccess: (res) => {
                        clearTimeout(redirectTimerRef);
                        const apiData = res?.data || res;
                        if (!apiData?.UserCode) throw new Error("Invalid user data from server.");

                        const user = {
                            id: apiData.UserCode,
                            name: apiData.ComplainantName || "Unknown User",
                            email: apiData.EmailAddress || "N/A",
                            mobile: apiData.MobileNumber || "N/A",
                            lastLogin: new Date().toLocaleString(),
                        };

                        const dummyToken = `secure-token-${Date.now()}`;
                        login(user, dummyToken);
                        setUserData(user);
                        setStatusMessage("Login successful! Redirecting...");
                        setLoading(false);
                        navigate("/");
                    },
                    onError: (err) => {
                        clearTimeout(redirectTimerRef);
                        console.error("API Error:", err);
                        setError("Authentication failed. Redirecting to PG Portal...");
                        setShowTimer(true);
                    },
                });
            } catch (err) {
                clearTimeout(redirectTimerRef);
                console.error("Authentication failed:", err.message);
                setError(err.message || "Invalid or corrupt payload received.");
                setShowTimer(true);
            }
        };

        const statePayload = payload;
        const urlParams = new URLSearchParams(window.location.search);
        const urlPayload = urlParams.get("payload")
            ? JSON.parse(decodeURIComponent(urlParams.get("payload")))
            : null;

        const payloadToUse = statePayload || urlPayload;

        // console.log("🔑 Payload to use:", payloadToUse);

        const decryptPayload = decryptFromBackend(payloadToUse);

        // console.log(`🔑 Decrypted Payload: ${decryptPayload}`, decryptPayload);


        if (!payloadToUse) {
            setError("No authentication payload provided.");
            setLoading(false);
            setShowTimer(true);
        } else if (!decryptPayload) {
            setError("Invalid or corrupted authentication payload.");
            setLoading(false);
            setShowTimer(true);
        } else {
            authenticate(decryptPayload);
        }
        // if (payloadToUse) {
        //     authenticate(payloadToUse);
        // } else {
        //     setError("No authentication payload provided.");
        //     setLoading(false);
        //     setShowTimer(true);
        // }

        return () => clearTimeout(redirectTimerRef);
    }, []);

    // Loading State with animated gradient
    if (loading && !error) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-600 via-purple-600 to-pink-500 text-center p-6 relative overflow-hidden">
                {/* Animated background orbs */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
                    <div className="absolute w-96 h-96 bg-blue-400/10 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>

                <div className="relative z-10 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-12 max-w-md border border-white/50">
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-2xl opacity-30 animate-pulse"></div>
                        <Loader2 className="relative w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
                    </div>

                    <div className="flex items-center justify-center gap-2 mb-4">
                        <Shield className="w-5 h-5 text-green-600 animate-pulse" />
                        <Sparkles className="w-5 h-5 text-yellow-500 animate-pulse" style={{ animationDelay: '0.5s' }} />
                    </div>

                    <h2 className="text-2xl font-bold text-gray-800 mb-3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        {statusMessage}
                    </h2>

                    <div className="space-y-2">
                        <p className="text-gray-600 text-sm flex items-center justify-center gap-2">
                            <Lock className="w-4 h-4 text-blue-600" />
                            {t('establishingSecureConnection')}
                        </p>

                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-full animate-pulse"></div>
                        </div>
                    </div>

                    <p className="text-gray-500 text-xs mt-6 flex items-center justify-center gap-1">
                        <Shield className="w-3 h-3" />
                        {t('encryptionActive')}
                    </p>
                </div>
            </div>
        );
    }

    // Error State with countdown timer
    if (error) {
        const progress = (redirectTimer / 5) * 100;

        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-red-500 via-pink-500 to-orange-500 relative overflow-hidden p-6">
                {/* Animated background */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
                    <div className="absolute w-96 h-96 bg-orange-400/10 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>

                <div className="relative z-10 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-10 max-w-md text-center border border-white/50">
                    <div className="relative mb-6">
                        <div className="absolute inset-0 bg-red-500 rounded-full blur-2xl opacity-20 animate-pulse"></div>
                        <div className="relative bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
                            <AlertCircle className="w-10 h-10 text-red-600 animate-pulse" />
                        </div>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('authenticationFailed')}</h2>
                    <p className="text-red-600 text-base font-medium mb-4">{error}</p>

                    {/* Countdown Timer */}
                    <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 mb-6 border border-red-200">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <Timer className="w-6 h-6 text-orange-600" />
                            <p className="text-gray-700 font-semibold text-lg">{t('returningIn')}</p>
                        </div>

                        <div className="relative w-32 h-32 mx-auto mb-4">
                            <svg className="w-full h-full transform -rotate-90">
                                <circle
                                    cx="64"
                                    cy="64"
                                    r="60"
                                    stroke="#fee2e2"
                                    strokeWidth="8"
                                    fill="none"
                                />
                                <circle
                                    cx="64"
                                    cy="64"
                                    r="60"
                                    stroke="url(#gradient)"
                                    strokeWidth="8"
                                    fill="none"
                                    strokeDasharray={`${2 * Math.PI * 60}`}
                                    strokeDashoffset={`${2 * Math.PI * 60 * (1 - progress / 100)}`}
                                    strokeLinecap="round"
                                    className="transition-all duration-1000 ease-linear"
                                />
                                <defs>
                                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#f97316" />
                                        <stop offset="100%" stopColor="#dc2626" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-5xl font-bold bg-gradient-to-br from-orange-600 to-red-600 bg-clip-text text-transparent">
                                    {redirectTimer}
                                </span>
                            </div>
                        </div>

                        <p className="text-gray-600 text-sm">
                            {t('returningToPGPortal')}
                        </p>
                    </div>

                    <button
                        onClick={() => window.location.href = redirectTo}
                        className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 rounded-xl font-semibold shadow-lg transition-all transform hover:scale-105"
                    >
                        {t('redirectNow')}
                    </button>
                </div>
            </div>
        );
    }

    // Success State - User authenticated
    if (userData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-emerald-400 via-cyan-500 to-blue-600 flex flex-col items-center justify-center p-6 relative overflow-hidden">
                {/* Animated background orbs */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute w-96 h-96 bg-white/20 rounded-full blur-3xl top-0 left-1/4 animate-pulse"></div>
                    <div className="absolute w-96 h-96 bg-blue-400/20 rounded-full blur-3xl bottom-0 right-1/4 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
                </div>

                {/* Security Badge */}
                {/* <div className="relative z-10 mb-6 flex items-center gap-2 bg-white/90 backdrop-blur-md text-green-700 px-6 py-3 rounded-full text-sm font-semibold shadow-xl border border-green-200">
                    <Shield className="w-5 h-5 animate-pulse" />
                    <span>🔐 Secure End-to-End Encrypted Session</span>
                    <CheckCircle2 className="w-5 h-5" />
                </div> */}

                <div className="relative z-10 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-10 w-full max-w-lg border border-white/50">
                    <div className="flex flex-col items-center space-y-6">
                        {/* Avatar with glow effect */}
                        <div className="relative">
                            {userData.image ? (
                                <>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-xl opacity-50 animate-pulse"></div>
                                    <img
                                        src={userData.image}
                                        alt={userData.name}
                                        className="relative w-28 h-28 rounded-full border-4 border-white shadow-2xl object-cover"
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-xl opacity-50 animate-pulse"></div>
                                    <div className="relative w-28 h-28 rounded-full border-4 border-white shadow-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-4xl font-bold">
                                        {userData.name?.charAt(0) || "U"}
                                    </div>
                                </>
                            )}
                            <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-2 border-4 border-white shadow-lg">
                                <CheckCircle2 className="w-5 h-5 text-white" />
                            </div>
                        </div>

                        {/* Welcome Message */}
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <Sparkles className="w-6 h-6 text-yellow-500 animate-pulse" />
                                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                    {t('greeting.welcomeMessage')}
                                </h1>
                                <Sparkles className="w-6 h-6 text-yellow-500 animate-pulse" style={{ animationDelay: '0.5s' }} />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800 mt-2">{userData.name}</h2>
                            <p className="text-gray-600 text-sm flex items-center gap-2 justify-center mt-3">
                                <Lock className="w-4 h-4 text-blue-600" />
                                {userData.role || "Authenticated User"}
                            </p>
                        </div>

                        {/* User Details Card */}
                        <div className="w-full bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 space-y-4 shadow-inner border border-blue-100">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 font-medium flex items-center gap-2">
                                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                    {t('userId')}
                                </span>
                                <span className="text-gray-800 font-semibold">{userData.id}</span>
                            </div>
                            <hr className="border-blue-200" />
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 font-medium flex items-center gap-2">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                                    {t('email')}
                                </span>
                                <span className="text-gray-800 font-semibold text-sm">{userData.email}</span>
                            </div>
                            <hr className="border-blue-200" />
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 font-medium flex items-center gap-2">
                                    <div className="w-2 h-2 bg-pink-600 rounded-full"></div>
                                    {t('mobile')}
                                </span>
                                <span className="text-gray-800 font-semibold">{userData.mobile}</span>
                            </div>
                            <hr className="border-blue-200" />
                            {/* <div className="flex justify-between items-center">
                                <span className="text-gray-600 font-medium flex items-center gap-2">
                                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                                    Last Login
                                </span>
                                <span className="text-gray-800 font-semibold text-sm">{userData.lastLogin}</span>
                            </div> */}
                        </div>

                        {/* Continue Button */}
                        <button
                            onClick={() => onLogin(userData)}
                            className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white py-4 rounded-xl font-bold shadow-2xl transition-all transform hover:scale-105 hover:shadow-3xl flex items-center justify-center gap-3 text-lg"
                        >
                            <MessageSquare className="w-6 h-6" />
                            {t('continueToChatbot')}
                            <Sparkles className="w-5 h-5" />
                        </button>

                        {/* <p className="text-gray-500 text-xs text-center flex items-center justify-center gap-1">
                            <Shield className="w-3 h-3" />
                            Your session is protected with military-grade encryption
                        </p> */}
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

LoginPage.propTypes = {
    onLogin: PropTypes.func.isRequired,
};

export default LoginPage;

